import AuthService from '@root/core/src/services/auth-service';
import ExpectedErrorResponseError from '@root/core/src/api/expected-error-response-error';
import Recase from '@root/vendor/recase';
import UnexpectedErrorResponseError from '@root/core/src/api/unexpected-error-response-error';
import environment from '@root/core/src/utils/environment';

const recase = Recase.create({});
const UUID_FORMAT = /[0-9a-f-]{36}/;
const CAPTCHA_TOKEN_FORMAT = /captcha_token=[A-Za-z0-9-_]+/;

/**
 * Utility class for making HTTP requests to the server API.
 *
 * Method variants:
 * - authenticated* methods (authenticatedGet, authenticatedPost, etc.) - include AUTH_TOKEN header from AuthService
 * - unauthenticated* methods (unauthenticatedGet, unauthenticatedPost, etc.) - do not include AUTH_TOKEN header
 *
 * Each method accepts "route" and "options" parameters
 *
 * Route parameter format: string path relative to API base URL (e.g., '/users/123', '/policies')
 *
 * Options parameter accepts the following keys:
 * - body: Object or FormData - request body (will be JSON.stringify'd and snake_cased unless FormData)
 * - query: Object - query parameters (will be snake_cased and appended to URL)
 * - trimNullQueryParams: boolean - when true, removes query params with null/undefined values (default: false)
 * - expectedResponse: number | number[] - expected successful status code(s) (default: 200)
 * - expectedErrorResponses: number[] - status codes that should throw ExpectedErrorResponseError instead of UnexpectedErrorResponseError (default: [])
 * - headers: Headers - additional headers (will be merged with default headers)
 * - method: string - HTTP method (automatically set by authenticatedGet, authenticatedPost, etc.)
 */
export default class ServerUtils {
  static async authenticatedGet(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(true),
      method: 'GET',
      ...options,
    });
  }

  static async unauthenticatedGet(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(false),
      method: 'GET',
      ...options,
    });
  }

  static async authenticatedPost(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(true),
      method: 'POST',
      ...options,
    });
  }

  static async unauthenticatedPost(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(false),
      method: 'POST',
      ...options,
    });
  }

  static async authenticatedPatch(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(true),
      method: 'PATCH',
      ...options,
    });
  }

  static async unauthenticatedPatch(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(false),
      method: 'PATCH',
      ...options,
    });
  }

  static async authenticatedPut(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(true),
      method: 'PUT',
      ...options,
    });
  }

  static async unauthenticatedPut(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(false),
      method: 'PUT',
      ...options,
    });
  }

  static async authenticatedDelete(route, options = {}) {
    return ServerUtils._request(route, {
      headers: ServerUtils._getHeaders(true),
      method: 'DELETE',
      ...options,
    });
  }

  static async _request(route, options = {}) {
    const query = ServerUtils._query(options);
    const trimNullQueryParams = options.trimNullQueryParams || false;
    const url = ServerUtils._buildUrl(route, query, trimNullQueryParams);

    const fetchOptions = {
      method: options.method,
      headers: options.headers,
    };

    if (ServerUtils._isFormData(options)) {
      fetchOptions.body = options.body;
      fetchOptions.headers.delete('Content-Type');
    } else {
      fetchOptions.body = ServerUtils._body(options);
    }

    const expectedResponse = options.expectedResponse || 200;
    const expectedErrorResponses = options.expectedErrorResponses || [];

    const response = await fetch(url, fetchOptions);

    let json = {};

    if (response.status !== 204) {
      try {
        json = await response.json();
      } catch (e) {
        json = {};
      }
    }

    ServerUtils.checkResponseStatus(response, expectedResponse, expectedErrorResponses, json.message, json);

    return {
      rawResponse: response,
      data: recase.camelCopy(json),
    };
  }

  static _buildUrl(path, query = {}, trimNullQueryParams = false) {
    const { apiBaseUrl } = environment;
    const url = `${apiBaseUrl}${path}`;
    const prunedQuery = trimNullQueryParams ? ServerUtils._trimNullValues(query) : query;

    const searchParams = new URLSearchParams();
    const queryKeys = Object.keys(prunedQuery);
    queryKeys.forEach((key) => searchParams.append(key, prunedQuery[key]));

    return queryKeys.length > 0 ? `${url}?${searchParams.toString()}` : url;
  }

  // Removes all keys from the object with a values of null or undefined
  static _trimNullValues(queryObject) {
    return Object.fromEntries(
      Object.entries(queryObject).filter(([_key, val]) => val !== null && val !== undefined)
    );
  }

  static _body(options = {}) {
    if (options.body) {
      return JSON.stringify(recase.snakeCopy(options.body));
    }
  }

  static _query(options = {}) {
    if (options.query) {
      return recase.snakeCopy(options.query);
    }
  }

  static checkResponseStatus(response, expected, expectedErrorResponses, errorMessage, jsonResponse) {
    const expectedArray = Array.isArray(expected) ? expected : [expected];
    if (expectedArray.includes(response.status)) {
      return;
    }

    if (expectedErrorResponses.includes(response.status)) {
      throw new ExpectedErrorResponseError(errorMessage, jsonResponse);
    }

    throw new UnexpectedErrorResponseError({
      message: `Unexpected ${response.status} on API call to ${ServerUtils._extractNormalizedUrlPath(response.url)}`,
      statusCode: response.status,
      url: response.url,
    });
  }

  static _extractNormalizedUrlPath(url) {
    if (!url) { return; }

    return '/' +
      url
        .split('/')
        .slice(3)
        .join('/')
        .replace(UUID_FORMAT, '(uuid)')
        .replace(CAPTCHA_TOKEN_FORMAT, 'captcha_token=(token)');
  }

  static _getHeaders(withAuthentication) {
    const headers = new Headers({
      Accept: 'application/json',
      'Appetize-Simulation': false,
      'Client-Api-Version': environment.apiVersion,
      'Client-App-Name': 'Root Web',
      'Client-Device': 'Browser',
      'Client-Framework': 'React',
      'Content-Type': 'application/json',
    });

    if (withAuthentication) {
      headers.append('AUTH_TOKEN', AuthService.getAccessToken());
    }

    return headers;
  }

  static _isFormData(options = {}) {
    return options.body instanceof FormData;
  }
}
